'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
// import Link from 'next/link'; — with the /platform link below
import { SiteNav } from './SiteNav';
import { Highlight } from './Highlight';
import { ArrowIcon } from './icons';
import { MinimalLoader } from '@/components/ui/minimal-loader';
// import { cultivPath } from '../site';
import { demoHref } from '../site';
import { getCultivLead } from '../lead-store';
import { newEventId, pushConversionEvent } from '@/lib/marketing-events';
import type { CultivCopy, CultivBooking } from '../types';
import { BOOKING_SLOTS, BOOKING_HORIZON_DAYS } from '@/lib/booking-slots';
import { brusselsDateKey } from '@/lib/brussels-time';
import { useDemoAvailability } from './useDemoAvailability';
import { BookingCalendar, dateKey, dateFromKey, formatBookingDate } from './BookingCalendar';
import { apiFetch } from '@/lib/api-client';

/* Calendly-like demo booking: calendar → time slots → contact form → done.
   Full-viewport fixed pixel scene (bg-09) with one floating product-UI frame.
   Free slots come from the host's Outlook calendar (GET /api/cultiv/availability);
   the POST creates a Teams meeting + notifies the team — app/api/cultiv/book/route.ts. */

type BookingStep = 'pick' | 'form' | 'done';

/** Set by /demo/success, which renders this page straight into its done state so
 *  a booking lands on its own URL — ad platforms count conversions by page. */
export type BookedSlot = { date: string; time: string };

export default function LandingDemoPage({
  copy,
  locale,
  booked,
}: {
  copy: CultivCopy;
  locale: string;
  booked?: BookedSlot;
}) {
  const booking: CultivBooking = copy.booking;
  const router = useRouter();
  const [date, setDate] = React.useState<Date | null>(booked ? dateFromKey(booked.date) : null);
  const [time, setTime] = React.useState<string | null>(booked?.time ?? null);
  const [step, setStep] = React.useState<BookingStep>(booked ? 'done' : 'pick');
  const [form, setForm] = React.useState({ name: '', email: '', agency: '', note: '', website: '' });
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [slotTaken, setSlotTaken] = React.useState(false);
  const [monthOffset, setMonthOffset] = React.useState(0);
  const slotsRef = React.useRef<HTMLDivElement>(null);

  const today = React.useMemo(() => dateFromKey(brusselsDateKey()), []);
  const max = React.useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + BOOKING_HORIZON_DAYS);
    return d;
  }, [today]);

  // one request per visible month, clamped to the bookable window
  const range = React.useMemo(() => {
    const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    const from = first < today ? today : first;
    const to = last > max ? max : last;
    return from > to ? null : { from: dateKey(from), to: dateKey(to) };
  }, [today, max, monthOffset]);

  const availability = useDemoAvailability(range?.from ?? null, range?.to ?? null);
  const { days: freeDays, fallbackDays, refresh: refreshAvailability, dropSlot } = availability;

  // `freeDays` only ever holds free/busy we actually read, so an empty list is real
  const isFull = React.useCallback(
    (day: Date) => freeDays[dateKey(day)]?.length === 0,
    [freeDays],
  );

  const selectedKey = date ? dateKey(date) : null;
  const dayFree = selectedKey ? freeDays[selectedKey] : undefined;
  /* Unreadable free/busy must never block a booking: offer the whole grid, silently.
     A visitor has no use for our plumbing — the team confirms the slot by email. */
  const fallback = !!selectedKey && fallbackDays.has(selectedKey);
  const slots = fallback ? BOOKING_SLOTS : dayFree;
  const slotsLoading = !!date && !fallback && !dayFree;
  const slotStatus = !date
    ? ''
    : slotsLoading
      ? booking.loadingSlots
      : slots && slots.length === 0
        ? booking.noSlots
        : '';
  const slotGroups = React.useMemo(() => {
    const list = slots ?? [];
    return [
      { id: 'morning', label: booking.morning, slots: list.filter((s) => s < '13:00') },
      { id: 'afternoon', label: booking.afternoon, slots: list.filter((s) => s >= '13:00') },
    ];
  }, [slots, booking.morning, booking.afternoon]);

  const pickDate = (d: Date) => {
    setDate(d);
    setTime(null);
    setSlotTaken(false);
  };

  // prefill from the session lead (left at the /product-tour gate) — after mount,
  // so SSR markup stays identical; never overwrite what the visitor already typed
  React.useEffect(() => {
    const lead = getCultivLead();
    if (!lead) return;
    setForm((f) => ({
      ...f,
      name: f.name || `${lead.firstName} ${lead.lastName}`.trim(),
      email: f.email || lead.email,
      agency: f.agency || lead.agency,
    }));
  }, []);

  // on phones the slots appear BELOW the calendar — bring them into view on date pick
  React.useEffect(() => {
    if (!date || !window.matchMedia('(max-width: 900px)').matches) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slotsRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [date]);
  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const longDate = date && formatBookingDate(date, locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const canSend = !!form.name.trim() && /.+@.+\..+/.test(form.email) && !sending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend || !date || !time) return;
    setSending(true);
    setError(false);
    setSlotTaken(false);
    try {
      // local getters, not toISOString() — Brussels slots must not shift a day via UTC
      const isoDate = dateKey(date);
      // same id on the pixel event and the server CAPI event → Meta dedups them
      const eventId = newEventId();
      const res = await apiFetch('/api/cultiv/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          agency: form.agency.trim(),
          note: form.note.trim(),
          website: form.website, // honeypot — humans never fill it
          date: isoDate,
          time,
          locale,
          eventId,
        }),
      });
      // 409 — someone took the slot while the page was open: back to a fresh grid
      if (res.status === 409) {
        setSlotTaken(true);
        setStep('pick');
        // drop it locally too: the refetch may be served from the API's short cache
        dropSlot(isoDate, time);
        setTime(null);
        refreshAvailability();
        return;
      }
      if (!res.ok) throw new Error(`book API ${res.status}`);
      pushConversionEvent('demo_booking', eventId, { email: form.email.trim() });
      /* The done state lives at its own URL so Google Ads (and any other platform
         that counts a destination page) can measure the booking without a tag. */
      router.replace(`${demoHref(locale)}/success?d=${isoDate}&t=${encodeURIComponent(time)}`);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    if (booked) {
      router.replace(demoHref(locale));
      return;
    }
    setDate(null);
    setTime(null);
    setError(false);
    setSlotTaken(false);
    setStep('pick');
  };

  return (
    <div className="b-root b-v2-bookpage" style={{ width: '100%' }}>
      <div className="b-book-bg"></div>
      <SiteNav copy={copy} locale={locale} />
      <main className="b-v2-bookstage" id="content">
        <div className="b-v2-bookframe">
          <div className="b-v2-book">

            {/* left — the meeting: kicker → title → lede → programme dot-list → time zone */}
            <aside className="b-book-info">
              <span className="b-v2-kick">Cultiv · {booking.eyebrow} · {booking.duration}</span>
              <h1><Highlight text={booking.title} /></h1>
              <p className="lede">{booking.lede}</p>
              <ul className="b-v2-dotlist">
                {booking.expect.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <span className="tz">{booking.tz}</span>
            </aside>

            {/* right — steps */}
            <div className="b-book-main">
              {/* mounted for the whole step so slot changes are announced: a live
                  region inserted together with its text is not read out */}
              <p className="b-sr-live" aria-live="polite">{slotStatus}</p>
              {step === 'pick' && (
                <>
                  <div className="b-book-step">
                    <span className="t">{date ? booking.pickTime : booking.pickDate}</span>
                    {date && <button className="b-book-back" onClick={() => { setDate(null); setTime(null); }}>{booking.back}</button>}
                  </div>
                  <div className={'b-book-cols' + (date ? ' two' : '')}>
                    <BookingCalendar
                      locale={locale} selected={date} onSelect={pickDate}
                      ariaPrev={copy.common.ariaPrev} ariaNext={copy.common.ariaNext}
                      today={today} max={max} monthOffset={monthOffset} onMonthOffset={setMonthOffset}
                      isFull={isFull}
                    />
                    {date && (
                      <div className="b-slots" ref={slotsRef}>
                        <span className="d">{formatBookingDate(date, locale, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        {slotTaken && <p className="note warn" role="alert">{booking.slotTaken}</p>}
                        {slotStatus && <p className="note">{slotStatus}</p>}
                        {slotsLoading ? (
                          <div className="grp" aria-hidden="true">
                            {[...Array(6)].map((_, i) => <span className="b-slot skel" key={i}></span>)}
                          </div>
                        ) : (
                          slotGroups.map((group) => group.slots.length > 0 && (
                            <React.Fragment key={group.id}>
                              <span className="g">{group.label}</span>
                              <div className="grp">
                                {group.slots.map((slot) => (
                                  <button className="b-slot" key={slot} onClick={() => { setTime(slot); setSlotTaken(false); setStep('form'); }}>{slot}</button>
                                ))}
                              </div>
                            </React.Fragment>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {step === 'form' && (
                <>
                  <div className="b-book-step">
                    <span className="t">{booking.formTitle}</span>
                    <button className="b-book-back" onClick={() => setStep('pick')}>{booking.back}</button>
                  </div>
                  <form className="b-book-form" onSubmit={submit}>
                    <div className="b-book-sum">{longDate} · {time} · {booking.duration}</div>
                    <div className="f"><label>{booking.nameLabel}</label><input value={form.name} onChange={setField('name')} autoFocus /></div>
                    <div className="f"><label>{booking.emailLabel}</label><input type="email" value={form.email} onChange={setField('email')} /></div>
                    <div className="f w"><label>{booking.agencyLabel}</label><input value={form.agency} onChange={setField('agency')} /></div>
                    <div className="f w"><label>{booking.noteLabel}</label><textarea value={form.note} onChange={setField('note')}></textarea></div>
                    {/* honeypot — visually hidden, tabbed over, bots fill it */}
                    <div className="f w b-book-hp" aria-hidden="true">
                      <label htmlFor="b-book-website">website</label>
                      <input id="b-book-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={setField('website')} />
                    </div>
                    {error && <p className="b-book-error" role="alert">{booking.submitError}</p>}
                    <div className="act">
                      <button type="submit" className="b-btn orange lg" disabled={!canSend}
                        aria-busy={sending} aria-label={sending ? booking.sending : undefined}
                        style={!canSend ? { opacity: 0.45, cursor: 'default' } : undefined}>
                        {sending ? (
                          <MinimalLoader size={18} color="white" />
                        ) : (
                          <>{booking.confirm} <ArrowIcon size={18} style={{ color: '#fff' }} /></>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}

              {step === 'done' && (
                <div className="b-book-done">
                  <h2><span className="dot"></span>{booking.doneTitle}</h2>
                  <span className="when">{longDate} · {time} · {booking.duration}</span>
                  <p>{booking.doneSub}</p>
                  {/* hidden while the /platform page is out of service
                  <div className="row">
                    <Link className="b-btn dark" href={cultivPath(locale, '/platform')}>{booking.fullDemo}</Link>
                  </div> */}
                  <button className="again" onClick={reset}>{booking.another}</button>
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
